<template>
  <div class="pagination" :class="{ 'in-pop': inPop }">
    <div class="pagination-body">
      <button 
        class="pagination-button prev02"
        @click="goToFirstPage"
        :disabled="currentPage === 1 || maxPage === 0"
      >
        <img :src="leftDoubleArrow" alt="처음 페이지 바로가기"/>
      </button>
      <button 
        class="pagination-button prev"
        @click="goToPrevPage"
        :disabled="currentPage === 1 || maxPage === 0"
      >
        <img :src="leftArrow" alt="이전 페이지 바로가기"/>
      </button>
      <ul class="pagination-list">
        <li
          v-for="page in visiblePages"
          :key="page"
          :class="currentPage === page && 'active'"
          >
        <button type="button" @click="goToPage(page)">{{ page }}</button>
        </li>
      </ul>
      <button 
        class="pagination-button next" 
        @click="goToNextPage"
        :disabled="currentPage === maxPage || maxPage === 0"
      >
        <img :src="rightArrow" alt="다음 페이지 바로가기"/>
      </button>
      <button 
        class="pagination-button next02" 
        @click="goToLastPage"
        :disabled="currentPage === maxPage || maxPage === 0"
      >
        <img :src="rightDoubleArrow" alt="마지막 페이지 바로 가기"/>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import leftDoubleArrow from "@/assets/images/pc/icon-hcon/icon-multi-arrow-left.svg";
import leftArrow from "@/assets/images/pc/icon-hcon/icon-arrow-left-md.svg";
import rightArrow from "@/assets/images/pc/icon-hcon/icon-arrow-left-md.svg";
import rightDoubleArrow from "@/assets/images/pc/icon-hcon/icon-multi-arrow-left.svg";

const emits = defineEmits(["update:currentPage"]);

const props = withDefaults(
  defineProps<{ inPop?: boolean; currentPage?: number; maxPage?: number, blockSize?: number }>(),
  {
    inPop: false,   // 팝업 내부 마진값 40
    currentPage: 1, // 현재 페이지
    maxPage: 10,    // 총 페이지 수
    blockSize: 10,  // 10페이지 씩 보여주기
  },
);

const startPage = computed(() => {
  return Math.floor((props.currentPage - 1) / props.blockSize ) * props.blockSize + 1;
});

const endPage = computed(() => {
  return Math.min(startPage.value + props.blockSize - 1, props.maxPage);
});

const visiblePages = computed(() => {
  const pages = [];
  for (let i = startPage.value; i <= endPage.value; i++) {
    pages.push(i);
  }
  return pages;
});

// 페이지 이동
const goToPage = (page: number) => {
  if (page < 1 || page > props.maxPage) return;
  emits("update:currentPage", page);
};

// ≪ 1페이지로 이동
const goToFirstPage = () => {
  emits("update:currentPage", 1);
}

// ＜ 이전 페이지로 이동
const goToPrevPage = () => {
  emits("update:currentPage", props.currentPage - 1);
}

// ＞ 다음 페이지로 이동
const goToNextPage = () => {
  emits("update:currentPage", props.currentPage + 1);
}

// ≫ 마지막 페이지로 이동
const goToLastPage = () => {
  emits("update:currentPage", props.maxPage);
}


</script>

<style lang="scss" scoped>
@use '@/assets/scss/pc/common/variables-hcon' as v;
@use '@/assets/scss/pc/import-hcon.scss' as *;

.pagination {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 40px;

  &.in-pop {
    margin: 40px 0;
  }

  &-body {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;

    font-weight: 400;
    color: v.$font-main-color;
  }

  &-list {
    display: flex;
    gap: 8px;
    cursor: pointer;

    li {
      button {
        font-size: 16px;
        width: 36px;
        height: 36px;
        color: v.$black-color;
        border-radius: v.$border-radius-sm;
        display: flex;
        align-items: center;
        justify-content: center;

        &:hover {
          background-color: #efefef;
        }
      }

      &.active {
        button {
          background-color: v.$primary-color;
          font-weight: 700;
          color: v.$white-color;
        }
      }
    }
  }

  .pagination-button {
    width: 36px;
    height: 36px;
    padding: 8px;
    border-radius: v.$border-radius-sm;

    &:hover {
      background-color: #efefef;
    }

    &.prev02,
    &.prev {
      transform: rotate(180deg);
    }
    
    &.next02,
    &.next {
      transform: rotate(0deg)
    }
  }
}
</style>
